import { defineComponent } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { minLength, required } from "@vuelidate/validators";

import { enumToMap } from "../../../../../../common/vue-helpers/modifiers/enum-modifiers";
import {
    Gender,
    CAHPSSurveyMethod,
    Relationships,
    PhoneTypeEnum,
    GenderPronoun,
    GenderPronouns
} from "../../../../../../enums/enums";
import { Address } from "../../../../../../resources-vue/vue-models/address";
import { IPhoneNumber } from "../../../../../../resources-vue/vue-interfaces/i-phone-number";
import { AuthorizedContact } from "../../../../../../resources-vue/vue-models/authorized-contact";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TagsInput from "../../../../../../resources-vue/vue-custom-elements/TagsInput/TagsInput.vue";
import PhoneNumber from "../../../../../../resources-vue/vue-custom-elements/PhoneNumber/PhoneNumber.vue";
import AddressForm from "../../../../../../resources-vue/vue-custom-elements/AddressForm/AddressForm.vue";

export default defineComponent({
    components: {
        FormSection,
        TagsInput,
        PhoneNumber,
        AddressForm
    },

    props: {
        isEditable: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        isPalliative: {
            type: Boolean,
            default: false
        },
        patientPrimaryAddress: {
            default: null
        },
        contactRolesEnum: {
            type: Array,
            default: []
        },
        relationshipsEnum: {
            type: Array<any>,
            default: []
        },
        cahpsSurveyContactEnum: {
            type: Array<any>,
            default: []
        },
        cahpsSurveyRecipientIndex: {
            default: -1
        },
        contact: {
            type: AuthorizedContact
        }
    },
    validations() {
        return {
            contact: {
                firstName: { required },
                lastName: { required },
                relationship: { required },
                phoneNumbers: [
                    {
                        number: { required, minLength: minLength(10) },
                        type: { required }
                    }
                ]
            }
        };
    },
    data(vm) {
        return {
            addrSameAsPatientAddr: false,
            selectedContactRoles: [],
            cahpsSurveyChecked: false,
            showRelationshipDescription: false,
            phoneNumberOptions: [PhoneTypeEnum.Home, PhoneTypeEnum.Mobile, PhoneTypeEnum.Work],
            Relationships: enumToMap(Relationships),
            GenderPronouns: enumToMap(GenderPronoun),
            GendersEnum: Gender,
            showOtherGenderPronounDescription: false,
            enumToMap,
            v$: useVuelidate()
        };
    },
    created() {
        this.init();
    },
    computed: {
        primaryType: {
            get() {
                return this.contact.phoneNumbers[this.primaryNumberIndex(this.contact.phoneNumbers)].type || 1;
            },
            set(value: any) {
                this.contact.phoneNumbers[this.primaryNumberIndex(this.contact.phoneNumbers)].type = value;
            }
        },
        secondaryType: {
            get() {
                return this.contact.phoneNumbers[this.alternativeNumberIndex(this.contact.phoneNumbers)].type || 1;
            },
            set(value: any) {
                return (this.contact.phoneNumbers[this.alternativeNumberIndex(this.contact.phoneNumbers)].type = value);
            }
        },
        isCahpsContact() {
            return (
                this.cahpsSurveyRecipientIndex !== -1 && this.cahpsSurveyRecipientIndex !== this.contact.contactOrder
            );
        }
    },
    unmounted() {
        this.addrSameAsPatientAddr = false;
    },
    watch: {
        addrSameAsPatientAddr(newValue: any) {
            this.addrSameAsPatientAddrChanged(newValue);
        },
        selectedContactRoles() {
            this.selectedContactRolesChanged();
        },
        cahpsSurveyChecked() {
            this.cahpsSurveyCheckedChanged();
        }
    },

    methods: {
        init() {
            if (!!this.contact) {
                this.handleRelationshipSelection();
                if (this.contact.contactRoles) {
                    this.selectedContactRoles = this.contactRolesEnum.filter((role: any) => {
                        return this.contact.contactRoles.some((item: any) => {
                            return role.value == item;
                        });
                    }) as any;
                }

                if (!this.contact.address) {
                    this.contact.address = new Address();
                } else if (this.contact.isUsingPrimaryAddress) {
                    this.addrSameAsPatientAddr = this.contact.isUsingPrimaryAddress;
                }

                if (this.primaryNumberIndex(this.contact.phoneNumbers) < 0) {
                    this.contact.phoneNumbers = this.contact.phoneNumbers.concat({
                        number: "",
                        extension: "",
                        type: 1,
                        isPrimary: true,
                        country: null
                    });
                }

                if (this.alternativeNumberIndex(this.contact.phoneNumbers) < 0) {
                    this.contact.phoneNumbers = this.contact.phoneNumbers.concat({
                        number: "",
                        extension: "",
                        type: 0,
                        isPrimary: false,
                        country: null
                    });
                }
                if (this.contact.cahpsSurvey > 0) {
                    this.cahpsSurveyChecked = true;
                }
            }
        },
        primaryNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (phoneNumbers) {
                return phoneNumbers.findIndex((e) => e.isPrimary);
            }
            return 0;
        },
        alternativeNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (phoneNumbers) {
                return phoneNumbers.findIndex((e) => !e.isPrimary);
            }
            return 0;
        },
        patientPrimaryAddressChanged() {
            if (!!this.patientPrimaryAddress && !!this.contact && this.contact.isUsingPrimaryAddress) {
                this.setAddressToPrimaryAddress();
            }
        },
        addrSameAsPatientAddrChanged(newValue: boolean) {
            if (newValue === true) {
                this.setAddressToPrimaryAddress();
                this.contact.isUsingPrimaryAddress = true;
            } else {
                this.contact.address = new Address();
                this.contact.isUsingPrimaryAddress = false;
            }
        },
        setAddressToPrimaryAddress() {
            if (!!this.patientPrimaryAddress && !!this.contact) {
                this.contact.address = Object.assign({}, this.patientPrimaryAddress);
            }
        },
        selectedContactRolesChanged() {
            if (this.selectedContactRoles) {
                this.contact.contactRoles = this.selectedContactRoles.map((role) => Number(role.value));
            }
        },
        cahpsSurveyCheckedChanged() {
            if (this.isPalliative) {
                this.contact.cahpsSurvey = this.cahpsSurveyChecked
                    ? Number(CAHPSSurveyMethod.enum.ReceiveCAHPSSurvey)
                    : 0;
            }
        },
        handleRelationshipSelection() {
            if (!!this.contact.relationship) {
                const isRelationshipOther: boolean = Relationships.enum.Other == this.contact.relationship;
                const isRelationshipOtherRelative: boolean =
                    Relationships.enum.OtherRelative == this.contact.relationship;
                this.showRelationshipDescription = isRelationshipOther || isRelationshipOtherRelative;
            } else {
                this.showRelationshipDescription = false;
            }
        },
        handleGenderPronounSelection() {
            if (!!this.contact.genderPronoun) {
                const isOther: boolean = GenderPronouns.Other == String(this.contact.genderPronoun);
                this.showOtherGenderPronounDescription = isOther;
            } else {
                this.showOtherGenderPronounDescription = false;
            }
        },
        selectedContactRolesUpdated(newValue: any) {
            this.selectedContactRoles = newValue;
        },
        updateAddress(value: any) {
            this.contact.address = value;
        },

        handleCahpsSurveyChanged() {
            if (this.contact.cahpsSurvey === 1 || this.contact.cahpsSurvey === 2) {
                this.contact.isCahpsSurveyRecipient = true;
            } else {
                this.contact.isCahpsSurveyRecipient = false;
            }
        }
    }
});
